<template>
    <modal size="lg" @close="reset(); $emit('close')" :height-max="true" ref="modal" :is-closeable="isCloseable" :autoshow="true">
        <template v-slot:title>
            <h3 class="modal-title">Company Tax Registration</h3>
        </template>

        <slot>
            <div v-if="step == 'jurisdiction'">
                <div class="form-row">
                    <div class="col-6">
                        <form-input label="Company" v-model="formData.company_id" :errors="errors.company_id" type="select" :options="companyOptions" />
                    </div>
                </div>

                <jurisdiction-finder ref="jurisdictionFinder" />
            </div>

            <div v-if="step == 'registration'">
                <p>Note that only taxes which are not already registered due to work locations will show up here.</p>

                <p v-if="!taxesByJurisdiction.length"><em>No taxes in the given jurisdictions can be found.</em></p>

                <div class="card mb-2" v-for="jt in taxesByJurisdiction" :key="jt.jurisdiction.id">
                    <div class="card-header pr-1 d-flex align-items-center">
                        <span class="btnlike mr-auto">
                            <h4 class="mb-0">{{ jt.jurisdiction.canonical_name }}</h4>
                        </span>

                    </div>

                    <div class="card-body py-1">
                        <div v-for="tax in jt.taxes" :key="tax.id">

                            <h5 class="mt-4 border-bottom">{{ tax.name }}</h5>

                            <tax-registration-form
                                ref="companyTaxRegistrationForms"
                                :tax-id="tax.id"
                                :jurisdiction-id="tax.jurisdiction_id"
                                :tax-type="tax.type"
                                :additional-fields="tax.current_version.employer_specific_fields"
                                :value="registrations[tax.id]"
                                @set-jurisdiction-filing-id="setJurisdictionFilingID($event)"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </slot>

        <template v-slot:footer>
            <form-errors :errors="formErrors"/>

            <button-row stacked>
                <button v-if="step == 'registration'" type="button" class="btn btn-outline-primary" @click.prevent="step = 'jurisdiction'">
                    Back
                </button>
                <button v-if="step == 'jurisdiction'" type="button" class="btn btn-outline-primary" @click.prevent="close()">
                    Cancel
                </button>
                <button v-if="step == 'jurisdiction'" type="button" class="btn btn-primary" @click.prevent="findTaxes()">
                    Next
                </button>
                <button v-if="step == 'registration'" type="button" class="btn btn-primary" @click.prevent="onSubmit()">
                    Save
                </button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'
import TaxRegistrationForm from './TaxRegistrationForm'
import JurisdictionFinder from '../JurisdictionFinder'

export default {
    props: ['client', 'taxRegistrations', ],
    mixins: [FormMixin, ModalMixin],
    components: {TaxRegistrationForm, JurisdictionFinder, },
    data() {
        return {
            formData: this.makeFormData(),
            step: 'jurisdiction',
            jurisdictions: {},
            taxes: [],
            registrations: [],
        }
    },
    computed: {
        companyOptions() {
            const result = []
            this.client.companies.forEach(c => {
                result.push({text: c.name, value: c.id})
            })

            result.sort((a, b) => a.text.localeCompare(b.text))
            return result
        },
        taxesByJurisdiction() {
            if (!this.taxes) {return []}
            const result = []
            this.taxes.forEach(t => {
                const j = this.jurisdictions[t.jurisdiction_id]

                const item = result.find(item => item.jurisdiction.id == j.id)
                if (item) {
                    item.taxes.push(t)
                }
                else {
                    result.push({
                        jurisdiction: j,
                        taxes: [t]
                    })
                }
            })

            return result
        },
    },
    methods: {
        getFormData() {
            const result = {}
            this.$refs.companyTaxRegistrationForms.forEach(f => {
                result[f.taxId] = f.getFormData()
            })

            return result
        },
        findTaxes() {
            this.errors = {}
            this.formErrors = []

            const jData = this.$refs.jurisdictionFinder.getFormData()
            let isValid = true
            if (jData.length < 1) {
                this.formErrors = ['Please select at least one jurisdictions.']
                isValid = false
            }

            if (!this.formData.company_id) {
                this.errors.company_id = ['Please select a company.']
                isValid = false
            }

            if (!isValid) {return}

            this.$store.dispatch('START_LOADING')

            const params = new URLSearchParams();
            jData.forEach(jId => params.append('jurisdiction_id', jId))

            this.$api.get(`/taxes-by-jurisdiction?${params.toString()}`).then(resp => {
                this.$store.dispatch('STOP_LOADING')

                const excludedTaxes = this.taxRegistrations.filter(tr => tr.company_id == this.formData.company_id).map(tr => tr.tax_id)
                this.taxes = resp.filter(t => !excludedTaxes.includes(t.id))

                this.jurisdictions = {}
                this.taxes.forEach(t => {
                    this.jurisdictions[t.jurisdiction.id] = t.jurisdiction
                })

            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors
                this.formErrors = errors.__all__
                this.onError()
            })

            this.step = 'registration'
        },
        setJurisdictionFilingID({jurisdictionId, filingId}) {
            this.$refs.companyTaxRegistrationForms.forEach(f => {
                if (f.jurisdictionId == jurisdictionId) {
                    f.setFilingId(filingId)
                }
            })
        },
        onSubmit() {
            let isValid = true
            this.$refs.companyTaxRegistrationForms.forEach(f => {
                isValid = f.validate() && isValid  // Obviously, don't reverse the order of this &&
            })

            if (!isValid) {return}

            this.$store.dispatch('START_LOADING')
            const url = `/clients/${this.client.id}/settings/companies/${this.formData.company_id}/tax-registrations`
            this.$api.post(url, {'taxes': this.getFormData()}).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showInfo(`Successfully added ${resp.length} tax registrations.`)
                this.$emit('updated')
                this.close()
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.$refs.companyTaxRegistrationForms.forEach(f => {
                    if (errors.hasOwnProperty(f.taxId)) {
                        f.setErrors(errors[f.taxId])
                    }
                })

                this.formErrors = (errors.__all__ || [])
                this.$bus.showError('Please check the form for any missing fields.')
            })
        }
    }
}
</script>
